import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Star, ChevronDown, ChevronUp, Gift, Map, Target, Shield, FileSignature, Eye, Brain, BarChart3, Clock, Flame, CheckCircle } from 'lucide-react';
import { trackInitiateCheckout } from '../utils/trackAddToCart';
import { OrderBump } from './OrderBump';
const TestimonialCard = ({
  name,
  role,
  quote,
  delay,
  image
}: {
  name: string;
  role: string;
  quote: string;
  delay: number;
  image: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  delay,
  duration: 0.5
}} className="bg-[#0a0a0a] border border-[#222] p-6 rounded-xl flex flex-col h-full" data-id="element-408">
    <div className="flex gap-1 mb-4" data-id="element-409">
      {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-[#d4af37] text-[#d4af37]" data-id="element-410" />)}
    </div>
    <p className="text-[#8a8578] italic text-sm mb-6 flex-grow leading-relaxed" data-id="element-411">
      "{quote}"
    </p>
    <div className="mt-auto flex items-center gap-3" data-id="element-412">
      <img src={image} alt={name} className="w-10 h-10 rounded-full object-cover border-2 border-[#d4af37]/30 flex-shrink-0" loading="lazy" decoding="async" data-id="element-413" />
      <div data-id="element-414">
        <p className="text-[#f5f0e8] font-bold text-sm" data-id="element-415">{name}</p>
        <p className="text-[#555] text-xs uppercase tracking-wide" data-id="element-416">{role}</p>
      </div>
    </div>
  </motion.div>;
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-[#222]" data-id="element-417">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex justify-between items-center text-left group" data-id="element-418">
        <span className={`text-lg font-medium transition-colors ${isOpen ? 'text-[#d4af37]' : 'text-[#f5f0e8] group-hover:text-[#d4af37]'}`} data-id="element-419">
          {question}
        </span>
        {isOpen ? <ChevronUp className="text-[#d4af37]" data-id="element-420" /> : <ChevronDown className="text-[#8a8578]" data-id="element-421" />}
      </button>
      <AnimatePresence data-id="element-422">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-423">
            <p className="text-[#8a8578] pb-6 leading-relaxed" data-id="element-424">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
interface OfferSectionProps {
  isOrderBumpChecked: boolean;
  onOrderBumpToggle: () => void;
}
export function OfferSection({
  isOrderBumpChecked,
  onOrderBumpToggle
}: OfferSectionProps) {
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  return <section id="comprar" className="w-full bg-[#050505] py-24 px-4 pb-40" data-id="element-425">
      <div className="max-w-5xl mx-auto" data-id="element-426">
        {/* INCLUSIONS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24" data-id="element-427">
          <div data-id="element-428">
            <h3 className="font-serif text-3xl text-[#f5f0e8] mb-8" data-id="element-429">
              Qué incluye el PDF
            </h3>
            <ul className="space-y-4 mb-8" data-id="element-430">
              {['Las 4 leyes mentales aterrizadas al trading', 'Explicación mente consciente vs subconsciente', 'Semáforo verde/amarillo/rojo', 'Checklist de 1 minuto para entrar', 'PP-7 completo con respuestas de intervención', 'Guiones de 60 segundos para cortar venganza', 'Cierre de sesión para evitar espiral', 'Diario mínimo (5 preguntas)', 'Plan de 14 días de instalación'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#8a8578]" data-id="element-431">
                  <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-432" />
                  <span data-id="element-433">{item}</span>
                </li>)}
            </ul>

            <div className="bg-[#0a0a0a] border border-[#d4af37]/30 p-6 rounded-lg" data-id="element-434">
              <h4 className="text-[#f5f0e8] font-medium mb-4" data-id="element-435">
                Esto es lo que cambia cuando lo aplicas:
              </h4>
              <ul className="space-y-3" data-id="element-436">
                <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-437">
                  <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-438" />
                  <span data-id="element-439">Dejas de mover el stop cuando no debes</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-440">
                  <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-441" />
                  <span data-id="element-442">Cortas operaciones antes del impulso</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-443">
                  <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-444" />
                  <span data-id="element-445">Ejecutas aunque no tengas ganas</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-446">
                  <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-447" />
                  <span data-id="element-448">Cierras sesiones sin espiral emocional</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-449">
                  <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-450" />
                  <span data-id="element-451">Dejas de "recuperar" y empiezas a respetar</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-8" data-id="element-452">
            <div className="bg-[#0a0a0a] border border-[#222] p-6 rounded-lg" data-id="element-453">
              <h4 className="text-[#f5f0e8] font-medium mb-4 flex items-center gap-2" data-id="element-454">
                <Check className="w-4 h-4 text-green-500" data-id="element-455" /> Esto es para ti si:
              </h4>
              <ul className="space-y-2 text-sm text-[#8a8578]" data-id="element-456">
                <li data-id="element-457">• Rompes reglas después de ganar o perder</li>
                <li data-id="element-458">• Te cuesta cerrar sesión</li>
                <li data-id="element-459">• Te da vergüenza admitir que te saboteas</li>
                <li data-id="element-460">• Quieres convertirte en el trader que admiras</li>
              </ul>
            </div>

            <div className="bg-[#0a0a0a] border border-[#222] p-6 rounded-lg opacity-70" data-id="element-461">
              <h4 className="text-[#f5f0e8] font-medium mb-4 flex items-center gap-2" data-id="element-462">
                <X className="w-4 h-4 text-red-500" data-id="element-463" /> No es para ti si:
              </h4>
              <ul className="space-y-2 text-sm text-[#8a8578]" data-id="element-464">
                <li data-id="element-465">• Buscas señales o entradas mágicas</li>
                <li data-id="element-466">• Quieres evitar pérdidas a toda costa</li>
                <li data-id="element-467">• Quieres espiritualidad sin estructura</li>
                <li data-id="element-468">• No estás dispuesto a seguir un protocolo</li>
              </ul>
            </div>
          </div>
        </div>

        {/* TESTIMONIALS */}
        <div className="mb-24" data-id="element-469">
          <h3 className="text-center font-serif text-3xl md:text-4xl text-[#f5f0e8] mb-4" data-id="element-470">
            No es magia. Es <span className="text-[#d4af37]" data-id="element-471">identidad.</span>
          </h3>
          <p className="text-center text-[#8a8578] mb-6 max-w-2xl mx-auto" data-id="element-472">
            Lo que sucede cuando dejas de pelear con el gráfico y empiezas a
            gobernar tus impulsos.
          </p>

          {/* Social proof badge */}
          <div className="flex justify-center mb-10" data-id="element-473">
            <div className="inline-flex items-center gap-2 bg-[#0a0a0a] border border-[#222] rounded-full px-5 py-2.5" data-id="element-474">
              <span className="relative flex h-2.5 w-2.5" data-id="element-475">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00C853] opacity-75" data-id="element-476"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00C853]" data-id="element-477"></span>
              </span>
              <span className="text-sm text-[#8a8578]" data-id="element-478">
                <span className="text-[#f5f0e8] font-bold" data-id="element-479">65+ traders</span> lo
                aplicaron en los últimos 2 meses
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-480">
            <TestimonialCard name="Carlos M." role="Trader de Forex · 3 años" image="https://cdn.magicpatterns.com/uploads/aba6L1M3RyiNovY4vEeKnw/www.thispersondoesnotexist.jpg" quote="Llevaba meses haciendo lo mismo: ganaba 2-3 días y lo devolvía en una sesión. No era estrategia. Era impulso. Apliqué el PP-7 literalmente el primer día… y por primera vez corté una operación cuando debía. No gané más. Pero dejé de perder como siempre perdía. Y eso cambió todo." delay={0} data-id="element-481" />
            <TestimonialCard name="Andrea R." role="Trader de Crypto · 1 año" image="https://cdn.magicpatterns.com/uploads/hfKzDsskmFD6dh6y2BJ1i6/www.thispersondoesnotexist.jpg" quote="Sabía exactamente dónde entrar, pero el miedo me paralizaba. Y cuando por fin entraba tarde, el mercado se daba la vuelta y yo entraba en pánico. El Diagnóstico de Termostato me mostró que mi problema era operar desde la escasez. Hoy ejecuto mi plan sin que me tiemble la mano. La diferencia es brutal." delay={0.1} data-id="element-482" />
            <TestimonialCard name="Miguel Á." role="Trader de Índices · 5 años" image="https://cdn.magicpatterns.com/uploads/qUu742s13PkLXEw3KhaiiB/www.thispersondoesnotexist.jpg" quote="Pensé que necesitaba otro curso de Smart Money. Estaba equivocado. Necesitaba dejar de ser mi propio enemigo. El Contrato del Ejecutor me obligó a poner reglas inquebrantables. Ya no opero para sentir adrenalina ni para 'vengarme' del mercado. Opero como un profesional aburrido, y por fin soy rentable." delay={0.2} data-id="element-483" />
          </div>
        </div>

        {/* PERSUASION GUARANTEE */}
        <div className="mb-24 max-w-3xl mx-auto" data-id="element-484">
          <div className="relative p-1 rounded-2xl bg-gradient-to-b from-[#d4af37]/50 to-transparent" data-id="element-485">
            <div className="bg-[#050505] rounded-xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-486">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-[#d4af37]/10 blur-3xl rounded-full pointer-events-none" data-id="element-487"></div>

              <Shield className="w-12 h-12 text-[#d4af37] mx-auto mb-6" data-id="element-488" />

              <h3 className="font-serif text-2xl md:text-3xl text-[#f5f0e8] mb-6" data-id="element-489">
                La Garantía de la Verdad Incómoda
              </h3>

              <div className="space-y-4 text-[#8a8578] text-lg leading-relaxed" data-id="element-490">
                <p data-id="element-491">
                  No te voy a prometer que te harás millonario en 30 días. Eso
                  es basura de marketing.
                </p>
                <p data-id="element-492">
                  Mi garantía es mucho más profunda:{' '}
                  <strong className="text-[#f5f0e8]" data-id="element-493">
                    Te garantizo que este manual te va a confrontar.
                  </strong>
                </p>
                <p data-id="element-494">
                  Te va a mostrar exactamente por qué te saboteas, por qué
                  devuelves tus ganancias y por qué sigues tropezando con la
                  misma piedra a pesar de "saber" lo que tienes que hacer.
                </p>
                <p className="text-[#d4af37] font-medium italic pt-4" data-id="element-495">
                  Si después de aplicar el protocolo de 14 días sigues operando
                  desde la necesidad, la venganza o el miedo... entonces el
                  problema no es emocional. Pero si te has sentido identificado
                  con cada palabra de esta página, este sistema te devolverá el
                  control.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* URGENCY BLOCK */}
        <div className="max-w-2xl mx-auto mb-8 text-center" data-id="element-496">
          <div className="inline-flex items-center gap-2 text-red-500 font-bold mb-4" data-id="element-497">
            <span className="animate-pulse" data-id="element-498">⚠️</span> Esto no va a estar así
            siempre
          </div>
          <p className="text-[#8a8578] text-sm md:text-base leading-relaxed" data-id="element-499">
            Este no es un producto pensado para venderse masivamente. Es una
            intervención directa. Y cuando empiece a escalar… el precio sube. No
            por marketing. Porque la gente que lo aplica… no vuelve atrás. Si
            estás viendo esto ahora… es porque llegaste antes de ese punto.{' '}
            <br data-id="element-500" />
            <br data-id="element-501" />
            <span className="text-[#f5f0e8] font-medium" data-id="element-502">
              👉 Después… no será $9.99.
            </span>
          </p>
        </div>

        {/* PRICING */}
        <div id="checkout" className="max-w-2xl mx-auto text-center mb-24 bg-[#0a0a0a] border border-[#222] rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-503">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#b8942e] via-[#d4af37] to-[#e8c84a]" data-id="element-504"></div>

          <div className="inline-block p-1 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] mb-8" data-id="element-505">
            <div className="bg-[#050505] rounded-full px-6 py-2" data-id="element-506">
              <span className="text-[#d4af37] text-sm font-bold tracking-widest uppercase" data-id="element-507">
                Oferta de lanzamiento
              </span>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mb-6" data-id="element-508">
            <div className="flex items-center gap-3 mb-2" data-id="element-509">
              <span className="text-[#8a8578] text-xl line-through decoration-red-500/50" data-id="element-510">
                {isOrderBumpChecked ? '$94 USD' : '$47 USD'}
              </span>
              <span className="bg-red-900/30 text-red-400 text-xs font-bold px-2 py-1 rounded" data-id="element-511">
                {isOrderBumpChecked ? '-79%' : '-79%'}
              </span>
            </div>
            <h2 className="text-5xl md:text-7xl font-serif text-[#f5f0e8] font-bold transition-all duration-300" data-id="element-512">
              ${isOrderBumpChecked ? '19.98' : '9.99'}{' '}
              <span className="text-xl md:text-3xl text-[#8a8578] font-normal" data-id="element-513">
                USD
              </span>
            </h2>
          </div>

          <p className="text-[#8a8578] text-lg mb-10 max-w-lg mx-auto" data-id="element-514">
            Es deliberadamente accesible. Porque el punto no es que pienses "qué
            caro". El punto es que pienses:{' '}
            <span className="text-[#f5f0e8]" data-id="element-515">
              "Es más caro seguir pagando el impuesto de mi impulsividad."
            </span>
          </p>

          {/* BOX 1: WHAT YOU'RE BUYING */}
          <div className="text-left bg-[#080808] border border-[#1a1a1a] rounded-xl p-6 mb-4 max-w-lg mx-auto" data-id="element-516">
            <p className="text-sm font-bold text-[#f5f0e8] mb-4" data-id="element-517">
              Estás comprando:
            </p>
            <ul className="space-y-3" data-id="element-518">
              <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-519">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-520" />
                <span data-id="element-521">Las 4 leyes mentales aterrizadas al trading</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-522">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-523" />
                <span data-id="element-524">PP-7 completo con respuestas de intervención</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-525">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-526" />
                <span data-id="element-527">RI-3: Reprogramación Identitaria en 14 días</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-528">
                <Check className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-529" />
                <span data-id="element-530">Semáforo + Checklist + Diario mínimo</span>
              </li>
            </ul>
          </div>

          {/* BOX 2: BONUSES */}
          <div className="text-left bg-[#080808] border border-[#1a1a1a] rounded-xl p-6 mb-10 max-w-lg mx-auto" data-id="element-531">
            <div className="flex items-center gap-2 mb-5" data-id="element-532">
              <Gift className="w-5 h-5 text-[#d4af37]" data-id="element-533" />
              <p className="text-sm font-bold text-[#d4af37]" data-id="element-534">
                + 3 Bonos GRATIS{' '}
                <span className="text-[#8a8578] font-normal" data-id="element-535">
                  (Valor{' '}
                  <span className="line-through decoration-red-500/50" data-id="element-536">
                    $47 USD
                  </span>
                  )
                </span>
              </p>
            </div>

            {/* Bonus compact list with images */}
            <div className="space-y-4" data-id="element-537">
              <div className="flex items-center gap-3" data-id="element-538">
                <img src="https://cdn.magicpatterns.com/uploads/qaQvkXWJZ8RDR5y9YY3mdE/Bono_1_Imagen.png" alt="Bono 1" className="w-12 h-12 rounded-lg object-cover border border-[#222] flex-shrink-0" loading="lazy" decoding="async" data-id="element-539" />
                <div data-id="element-540">
                  <p className="text-sm font-medium text-[#f5f0e8]" data-id="element-541">
                    Anti-Autosabotaje
                  </p>
                  <p className="text-xs text-[#8a8578]" data-id="element-542">
                    Mapa de Gatillos + Plan de Intervención
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3" data-id="element-543">
                <img src="https://cdn.magicpatterns.com/uploads/ooH1SwCackC2np9RuWDeDK/Bono_2.png" alt="Bono 2" className="w-12 h-12 rounded-lg object-cover border border-[#222] flex-shrink-0" loading="lazy" decoding="async" data-id="element-544" />
                <div data-id="element-545">
                  <p className="text-sm font-medium text-[#f5f0e8]" data-id="element-546">
                    Contrato del Ejecutor
                  </p>
                  <p className="text-xs text-[#8a8578]" data-id="element-547">
                    Identidad Trader Rentable (RI-3 en 14 días)
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3" data-id="element-548">
                <img src="https://cdn.magicpatterns.com/uploads/cpYUmPGveHC3u7DvfiMsDs/Bono_3.png" alt="Bono 3" className="w-12 h-12 rounded-lg object-cover border border-[#222] flex-shrink-0" loading="lazy" decoding="async" data-id="element-549" />
                <div data-id="element-550">
                  <p className="text-sm font-medium text-[#f5f0e8]" data-id="element-551">
                    Rutina PDF (3 min)
                  </p>
                  <p className="text-xs text-[#8a8578]" data-id="element-552">
                    Inicio + En Caliente + Cierre de Sesión
                  </p>
                </div>
              </div>
            </div>

            {/* EXPANDABLE DETAIL */}
            <AnimatePresence data-id="element-553">
              {showBonusDetails && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-554">
                  <div className="pt-5 mt-5 border-t border-[#1a1a1a] space-y-8" data-id="element-555">
                    {/* BONO #1 DETAIL */}
                    <div data-id="element-556">
                      <div className="flex items-center gap-2 mb-3" data-id="element-557">
                        <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-[10px] font-bold tracking-wider" data-id="element-558">
                          BONO #1
                        </span>
                        <span className="text-xs text-[#8a8578]" data-id="element-559">
                          Valor: <span className="line-through" data-id="element-560">$17 USD</span> |{' '}
                          <span className="text-[#d4af37]" data-id="element-561">GRATIS</span>
                        </span>
                      </div>
                      <h4 className="font-serif text-lg text-[#f5f0e8] mb-2" data-id="element-562">
                        Anti-Autosabotaje: Mapa de Gatillos + Plan de
                        Intervención
                      </h4>
                      <p className="text-[#8a8578] text-xs mb-3" data-id="element-563">
                        Este es el bono para el momento donde más te traicionas:
                        cuando tu mente empieza a negociar con la misma frase de
                        siempre…
                      </p>
                      <div className="pl-3 border-l-2 border-[#d4af37]/30 mb-3 italic text-[#d4af37]/80 text-xs space-y-0.5" data-id="element-564">
                        <p data-id="element-565">"Solo un trade más."</p>
                        <p data-id="element-566">"Solo para recuperar."</p>
                        <p data-id="element-567">"Solo porque está clarísimo."</p>
                      </div>
                      <p className="text-[f5f0e8] text-xs font-medium mb-3" data-id="element-568">
                        Aquí no hay teoría. Hay intervención.
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-[#555] mb-2" data-id="element-569">
                        Qué te da:
                      </p>
                      <ul className="space-y-2" data-id="element-570">
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-571">
                          <Map className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-572" />
                          <span data-id="element-573">
                            Un mapa visual de los 7 gatillos que te cuestan
                            dinero y paz
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-574">
                          <Target className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-575" />
                          <span data-id="element-576">
                            Un plan para identificar tu gatillo dominante del
                            día en 90 segundos
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-577">
                          <Shield className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-578" />
                          <span data-id="element-579">
                            La respuesta mecánica para cortar el impulso antes
                            del pico
                          </span>
                        </li>
                      </ul>
                      <p className="text-xs italic text-[#d4af37] mt-3" data-id="element-580">
                        "Porque el autosabotaje no se vence con ganas. Se vence
                        con un protocolo que se activa solo."
                      </p>
                    </div>

                    {/* BONO #2 DETAIL */}
                    <div data-id="element-581">
                      <div className="flex items-center gap-2 mb-3" data-id="element-582">
                        <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-[10px] font-bold tracking-wider" data-id="element-583">
                          BONO #2
                        </span>
                        <span className="text-xs text-[#8a8578]" data-id="element-584">
                          Valor: <span className="line-through" data-id="element-585">$17 USD</span> |{' '}
                          <span className="text-[#d4af37]" data-id="element-586">GRATIS</span>
                        </span>
                      </div>
                      <h4 className="font-serif text-lg text-[#f5f0e8] mb-2" data-id="element-587">
                        Identidad Trader Rentable: El Contrato del Ejecutor
                      </h4>
                      <p className="text-[#8a8578] text-xs mb-3" data-id="element-588">
                        Este bono es confrontativo, porque es lo que casi nadie
                        quiere aceptar:
                      </p>
                      <div className="pl-3 border-l-2 border-[#d4af37]/30 mb-3 italic text-[#d4af37]/80 text-xs" data-id="element-589">
                        <p data-id="element-590">
                          "Si no está escrito, se negocia. Si se negocia, se
                          rompe. Y si se rompe, tu identidad se parte en dos."
                        </p>
                      </div>
                      <p className="text-[10px] uppercase tracking-widest text-[#555] mb-2" data-id="element-591">
                        Qué te da:
                      </p>
                      <ul className="space-y-2" data-id="element-592">
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-593">
                          <FileSignature className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-594" />
                          <span data-id="element-595">
                            Un Contrato del Ejecutor (firmable) con reglas no
                            negociables
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-596">
                          <Eye className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-597" />
                          <span data-id="element-598">
                            Enfoque brutal en ambiente (setup, redes,
                            notificaciones, fatiga)
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-599">
                          <Brain className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-600" />
                          <span data-id="element-601">
                            El ciclo: Ambiente → Pensamiento → Sentimiento →
                            Acción → Resultado
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-602">
                          <BarChart3 className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-603" />
                          <span data-id="element-604">
                            Sistema de evidencia diaria (60 seg) que instala
                            identidad
                          </span>
                        </li>
                      </ul>
                      <p className="text-xs italic text-[#d4af37] mt-3" data-id="element-605">
                        "Porque el trader rentable no es el que sabe más. Es el
                        que ejecuta igual cuando gana, cuando pierde y cuando el
                        ego aprieta."
                      </p>
                    </div>

                    {/* BONO #3 DETAIL */}
                    <div data-id="element-606">
                      <div className="flex items-center gap-2 mb-3" data-id="element-607">
                        <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-[10px] font-bold tracking-wider" data-id="element-608">
                          BONO #3
                        </span>
                        <span className="text-xs text-[#8a8578]" data-id="element-609">
                          Valor: <span className="line-through" data-id="element-610">$13 USD</span> |{' '}
                          <span className="text-[#d4af37]" data-id="element-611">GRATIS</span>
                        </span>
                      </div>
                      <h4 className="font-serif text-lg text-[#f5f0e8] mb-2" data-id="element-612">
                        Rutina PDF: Inicio + En Caliente + Cierre de Sesión
                      </h4>
                      <p className="text-[#8a8578] text-xs mb-3" data-id="element-613">
                        Si tu sesión empieza sin ritual, empieza con
                        improvisación. Y si termina sin cierre, termina con
                        espiral.
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-[#555] mb-2" data-id="element-614">
                        Qué te da:
                      </p>
                      <ul className="space-y-2" data-id="element-615">
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-616">
                          <Clock className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-617" />
                          <span data-id="element-618">
                            Inicio en 90 seg: semáforo + regla del día +
                            checklist
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-619">
                          <Flame className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-620" />
                          <span data-id="element-621">
                            Intervención en caliente en 45 seg: pausa +
                            respiración + regla mecánica
                          </span>
                        </li>
                        <li className="flex items-start gap-2 text-xs text-[#8a8578]" data-id="element-622">
                          <CheckCircle className="w-3.5 h-3.5 text-[#d4af37] mt-0.5 flex-shrink-0" data-id="element-623" />
                          <span data-id="element-624">
                            Cierre en 90 seg: evidencia mínima + ajuste para
                            mañana (sin castigo)
                          </span>
                        </li>
                      </ul>
                      <p className="text-xs italic text-[#d4af37] mt-3" data-id="element-625">
                        "Porque el autosabotaje casi siempre ocurre cuando
                        entras sin estado o te quedas emocionalmente abierto al
                        cerrar."
                      </p>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* LEER MÁS TOGGLE */}
            <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="flex items-center gap-1.5 mx-auto mt-5 pt-4 border-t border-[#1a1a1a] text-sm text-[#d4af37] hover:text-[#e8c84a] transition-colors" data-id="element-626">
              <span data-id="element-627">{showBonusDetails ? 'Leer menos' : 'Leer más'}</span>
              {showBonusDetails ? <ChevronUp className="w-4 h-4" data-id="element-628" /> : <ChevronDown className="w-4 h-4" data-id="element-629" />}
            </button>
          </div>

          <div className="mt-8 mb-6 text-center" data-id="element-630">
            <p className="text-[#8a8578] text-sm italic" data-id="element-631">
              Un solo trade impulsivo… puede costarte más que esto.
              <br data-id="element-632" />
              Pero no es el dinero. Es confirmar, otra vez… que no estás en
              control.
              <br data-id="element-633" />
              <span className="text-[#f5f0e8] font-bold not-italic mt-2 block" data-id="element-634">
                👇 Corrige eso hoy.
              </span>
            </p>
          </div>

          <a href="https://pay.hotmart.com/I104339970M?checkoutMode=10" target="_blank" rel="noopener noreferrer" onClick={() => trackInitiateCheckout()} className="inline-block w-full md:w-auto px-12 py-4 bg-gradient-to-r from-[#b8942e] via-[#d4af37] to-[#e8c84a] text-black font-bold text-lg tracking-wide rounded hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300 transform hover:-translate-y-1" data-id="element-635">
            QUIERO DEJAR DE SABOTEARME HOY
          </a>

          <p className="mt-4 text-xs text-[#555]" data-id="element-636">
            Entrega digital inmediata (PDF)
          </p>

          <div className="max-w-xl mx-auto" data-id="element-637">
            <OrderBump isChecked={isOrderBumpChecked} onToggle={onOrderBumpToggle} data-id="element-638" />

            {/* PRICE BREAKDOWN */}
            <div className="mt-8 bg-[#0a0a0a] border border-[#222] rounded-xl p-6 text-left" data-id="element-639">
              <div className="space-y-3" data-id="element-640">
                <div className="flex justify-between items-center text-sm" data-id="element-641">
                  <span className="text-[#8a8578]" data-id="element-642">Trading en Paz (PDF)</span>
                  <div className="flex items-center gap-2" data-id="element-643">
                    <span className="text-[#555] line-through text-xs" data-id="element-644">
                      $47
                    </span>
                    <span className="text-[#f5f0e8] font-medium" data-id="element-645">$9.99</span>
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm" data-id="element-646">
                  <span className="text-[#8a8578]" data-id="element-647">3 Bonos de Lujo</span>
                  <div className="flex items-center gap-2" data-id="element-648">
                    <span className="text-[#555] line-through text-xs" data-id="element-649">
                      $47
                    </span>
                    <span className="text-[#00C853] font-medium" data-id="element-650">GRATIS</span>
                  </div>
                </div>

                {isOrderBumpChecked && <div className="flex justify-between items-center text-sm" data-id="element-651">
                    <span className="text-[#8a8578]" data-id="element-652">
                      Psicología del Dinero
                    </span>
                    <div className="flex items-center gap-2" data-id="element-653">
                      <span className="text-[#555] line-through text-xs" data-id="element-654">
                        $59
                      </span>
                      <span className="text-[#f5f0e8] font-medium" data-id="element-655">$9.99</span>
                    </div>
                  </div>}

                <div className="border-t border-[#222] pt-3 mt-3" data-id="element-656">
                  <div className="flex justify-between items-center text-sm mb-1" data-id="element-657">
                    <span className="text-[#8a8578]" data-id="element-658">Valor Total</span>
                    <span className="text-[#555] line-through text-xs" data-id="element-659">
                      {isOrderBumpChecked ? '$153' : '$94'}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center" data-id="element-660">
                  <span className="text-[#d4af37] font-bold text-lg" data-id="element-661">
                    Hoy pagas solo
                  </span>
                  <span className="text-[#f5f0e8] font-bold text-2xl font-serif" data-id="element-662">
                    ${isOrderBumpChecked ? '19.98' : '9.99'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 14 DAY PLAN */}
        <div className="border-t border-[#222] pt-16 mb-24" data-id="element-663">
          <h3 className="text-center font-serif text-2xl text-[#f5f0e8] mb-12" data-id="element-664">
            La Regla de 14 Días
          </h3>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#8a8578] max-w-4xl mx-auto" data-id="element-665">
            <div className="flex flex-col items-center gap-2" data-id="element-666">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-667">
                1
              </span>
              <span data-id="element-668">Semáforo</span>
            </div>
            <div className="h-px w-8 bg-[#333] hidden md:block" data-id="element-669"></div>
            <div className="flex flex-col items-center gap-2" data-id="element-670">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-671">
                2
              </span>
              <span data-id="element-672">Checklist</span>
            </div>
            <div className="h-px w-8 bg-[#333] hidden md:block" data-id="element-673"></div>
            <div className="flex flex-col items-center gap-2" data-id="element-674">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-675">
                3
              </span>
              <span data-id="element-676">PP-7</span>
            </div>
            <div className="h-px w-8 bg-[#333] hidden md:block" data-id="element-677"></div>
            <div className="flex flex-col items-center gap-2" data-id="element-678">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-679">
                4
              </span>
              <span data-id="element-680">Cierre</span>
            </div>
            <div className="h-px w-8 bg-[#333] hidden md:block" data-id="element-681"></div>
            <div className="flex flex-col items-center gap-2" data-id="element-682">
              <span className="w-8 h-8 rounded-full border border-[#333] flex items-center justify-center text-[#d4af37]" data-id="element-683">
                5
              </span>
              <span data-id="element-684">Diario</span>
            </div>
          </div>
        </div>

        {/* FAQ SECTION */}
        <div className="max-w-3xl mx-auto mb-24" data-id="element-685">
          <h3 className="text-center font-serif text-2xl text-[#f5f0e8] mb-12" data-id="element-686">
            Preguntas Frecuentes
          </h3>
          <div className="space-y-2" data-id="element-687">
            <FAQItem question="¿Es solo un PDF?" answer="Sí, y eso es intencional. No necesitas otro curso de 40 horas que no vas a terminar. Necesitas un sistema simple que puedas imprimir, pegar donde operas, y aplicar HOY. Trading en Paz está diseñado para ser una herramienta de uso diario, no contenido para consumir y olvidar." data-id="element-688" />
            <FAQItem question="¿Funciona para mi mercado (crypto, forex, acciones, índices)?" answer="Sí. El sabotaje emocional no distingue entre mercados. Si operas con dinero real y sientes presión, este sistema aplica. Los gatillos (FOMO, venganza, euforia) son universales." data-id="element-689" />
            <FAQItem question="¿Cuánto tiempo toma ver resultados?" answer="El plan está diseñado para 14 días. No vas a convertirte en otro trader de la noche a la mañana, pero en 14 días de aplicación consistente vas a notar algo poderoso: empiezas a verte diferente. Y cuando te ves diferente, operas diferente." data-id="element-690" />
            <FAQItem question="¿Esto reemplaza mi estrategia de trading?" answer="No. Trading en Paz no te dice cuándo entrar o salir del mercado. Te da el sistema mental para que EJECUTES tu estrategia sin sabotearte. Es el complemento que tu estrategia necesita para funcionar de verdad." data-id="element-691" />
            <FAQItem question="¿Por qué tan barato?" answer="Porque una sola sesión impulsiva te cuesta más que esto. El precio no es el punto. El punto es que dejes de pagar el impuesto invisible de tu impulsividad. Si $9.99 te parece mucho, piensa en cuánto te costó tu último trade de venganza." data-id="element-692" />
            <FAQItem question="¿Qué pasa si no me funciona?" answer="Si aplicas el protocolo 14 días y no notas ningún cambio en tu forma de operar, el problema probablemente no es emocional — es estratégico. Pero si te reconoces en esta página, este PDF fue escrito para ti." data-id="element-693" />
          </div>
        </div>

        {/* CLOSING */}
        <div className="bg-[#050505] rounded-2xl border border-[#222] p-8 md:p-16 relative overflow-hidden max-w-4xl mx-auto" data-id="element-694">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent opacity-50" data-id="element-695"></div>

          <div className="max-w-2xl mx-auto text-center space-y-8" data-id="element-696">
            <h3 className="font-serif text-2xl md:text-4xl text-[#f5f0e8] leading-tight" data-id="element-697">
              Hay algo que no puedes desver… <br data-id="element-698" />
              <span className="text-[#8a8578] text-xl md:text-2xl font-light mt-2 block" data-id="element-699">
                porque ya lo sentiste mientras leías esto.
              </span>
            </h3>

            <div className="space-y-6 text-[#8a8578] text-base md:text-lg leading-relaxed" data-id="element-700">
              <p data-id="element-701">
                Ese momento exacto… donde sabes lo que tienes que hacer… y aun
                así haces lo contrario.
              </p>

              <p data-id="element-702">
                No es falta de disciplina. No es falta de ganas.
                <br data-id="element-703" />
                <span className="text-[#f5f0e8] font-medium" data-id="element-704">
                  Es que, en ese instante… no eres tú el que decide.
                </span>
              </p>

              <p className="text-[#d4af37] font-medium italic" data-id="element-705">
                Y eso… es peligroso.
              </p>

              <p data-id="element-706">
                Porque mientras sigas operando así… no importa cuánto aprendas…
                no importa cuántas estrategias pruebes… vas a volver al mismo
                punto.
              </p>

              <div className="py-4 border-y border-[#222] my-8" data-id="element-707">
                <p className="text-[#f5f0e8]" data-id="element-708">Otra vez frente a la pantalla.</p>
                <p className="text-[#f5f0e8]" data-id="element-709">
                  Otra vez prometiéndote que ahora sí.
                </p>
                <p className="text-[#f5f0e8]" data-id="element-710">
                  Otra vez rompiendo esa promesa.
                </p>
              </div>

              <p data-id="element-711">
                Y en el fondo… ya sabes lo que pasa después.
                <br data-id="element-712" />
                Silencio. Frustración. Y esa sensación… de que te estás fallando
                a ti mismo.
              </p>

              <p data-id="element-713">
                Ahora mismo… estás en uno de esos momentos raros.
                <br data-id="element-714" />
                Un momento donde ves el patrón completo. Donde no puedes culpar
                al mercado. Donde no puedes decir "no sabía".
              </p>

              <p className="text-[#f5f0e8] font-medium" data-id="element-715">
                Y eso incomoda. Porque te deja con una sola verdad:
              </p>

              <div className="bg-[#0a0a0a] border border-[#d4af37]/30 p-6 rounded-lg my-8" data-id="element-716">
                <p className="text-[#f5f0e8] font-bold text-xl mb-2" data-id="element-717">
                  👉 Si te vas de esta página sin hacer nada…
                </p>
                <p className="text-[#8a8578]" data-id="element-718">
                  no es falta de información.{' '}
                  <span className="text-[#d4af37]" data-id="element-719">Es una decisión.</span>
                </p>
                <p className="mt-4" data-id="element-720">
                  La decisión de seguir siendo el mismo trader… que ya sabes
                  cómo termina. Y no hace falta que lo imagines… porque ya lo
                  viviste. Más de una vez.
                </p>
              </div>

              <p data-id="element-721">
                Ahora… puedes cerrar esta página… y confiar en que "mañana sí".
              </p>

              <p className="text-[#f5f0e8] font-bold text-xl" data-id="element-722">
                O puedes hacer algo distinto por primera vez.
              </p>

              <p className="text-[#8a8578]" data-id="element-723">
                No cuando te sientas listo. No cuando tengas ganas.{' '}
                <span className="text-[#d4af37] font-bold uppercase tracking-widest" data-id="element-724">
                  Ahora.
                </span>
              </p>
            </div>

            <div className="pt-8" data-id="element-725">
              <p className="text-[#f5f0e8] font-bold mb-6 text-2xl animate-bounce" data-id="element-726">
                👇
              </p>
              <a href="https://pay.hotmart.com/I104339970M?checkoutMode=10" target="_blank" rel="noopener noreferrer" onClick={() => trackInitiateCheckout()} className="inline-block w-full md:w-auto px-12 py-5 bg-gradient-to-r from-[#b8942e] via-[#d4af37] to-[#e8c84a] text-black font-bold text-lg tracking-wide rounded hover:shadow-[0_0_40px_rgba(212,175,55,0.5)] transition-all duration-300 transform hover:-translate-y-1" data-id="element-727">
                QUIERO DEJAR DE SABOTEARME HOY
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}